import { useRef } from "react";
import { useI18n } from "../i18n/index.js";
import { avatarDataUrl } from "../lib/avatar.js";
import { Avatar, Button } from "../ui/index.js";

/**
 * The right-hand side of an avatar setting: the avatar itself opens the file picker, and
 * "Remove" appears once there is a picture. The caller holds the value and submits it.
 */
export function AvatarPicker({
  name,
  value,
  onChange,
  onUnreadable,
  disabled = false,
  round = false,
}: {
  /** Whose avatar: its first letter stands in when there is no picture. */
  name: string;
  value: string | null;
  onChange: (value: string | null) => void;
  /** Called when the browser cannot decode the chosen file. */
  onUnreadable: () => void;
  disabled?: boolean;
  /** People are round, workspaces are square. */
  round?: boolean;
}) {
  const { t } = useI18n();
  const picker = useRef<HTMLInputElement>(null);
  return (
    <>
      {value && (
        <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => onChange(null)}>
          {t("avatar.remove")}
        </Button>
      )}
      <button type="button" className="dt-avatar-button" data-round={round || undefined} aria-label={t("avatar.upload")} disabled={disabled} onClick={() => picker.current?.click()}>
        <Avatar name={name} src={value} size={40} round={round} />
      </button>
      <input
        ref={picker}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = ""; // picking the same file again must fire change again
          if (!file) return;
          try {
            onChange(await avatarDataUrl(file));
          } catch {
            onUnreadable();
          }
        }}
      />
    </>
  );
}
